import { createSlice } from '@reduxjs/toolkit';
import { cloneDeep } from 'lodash';

import { UPLOAD_STATUS_ENUM } from '@pages/organizer/Cloud/const/upload/upload-status';

import type {
  PreviewFileType,
  ProgressType,
  UploadCounts,
  UploadsType,
} from '@pages/organizer/Cloud/store/upload-store/upload-types';
import type { PayloadAction } from '@reduxjs/toolkit';

type UploadState = {
  expand_popup: boolean;
  visible_popup: boolean;
  progress: ProgressType;
  uploads: UploadsType;
  uploadCounts: UploadCounts;
};

const initialState: UploadState = {
  expand_popup: false,
  visible_popup: false,
  progress: {},
  uploads: {},
  uploadCounts: {
    total_upload_files: 0,
    is_upload_complete: true,
    uploaded_files: 0,
    failed_upload_count: 0,
    canceled_upload_count: 0,
  },
};

const uploadSlice = createSlice({
  name: 'upload-slice',
  initialState,
  reducers: {
    setProgress(state, action: PayloadAction<{ preview_id: string; loaded: number }>) {
      const { preview_id, loaded } = action.payload;
      state.progress[preview_id] = loaded;
    },

    setUploadFile(state, action: PayloadAction<{ file: PreviewFileType }>) {
      state.uploads[action.payload.file.preview_id] = action.payload.file;
    },
    expandPopup(state, action: PayloadAction<{ expand: boolean }>) {
      state.expand_popup = action.payload.expand;
    },
    visiblePopup(state, action: PayloadAction<{ visible: boolean }>) {
      state.visible_popup = action.payload.visible;
    },
    updateUpload(state, action: PayloadAction<{ preview_id: string; changes: Partial<PreviewFileType> }>) {
      const { preview_id, changes } = action.payload;
      const file = state.uploads[preview_id];

      if (file) {
        state.uploads[preview_id] = {
          ...file,
          ...changes,
        };
      }
    },
    removeUpload(state, action: PayloadAction<{ preview_id: string }>) {
      const file = state.uploads[action.payload.preview_id];
      if (file) delete state.uploads[action.payload.preview_id];

      const uploads = Object.keys(state.uploads).length;
      if (!uploads) {
        uploadSlice.caseReducers.resetUploadState(state);
        state.expand_popup = false;
        state.visible_popup = false;
      }
    },

    uploadStart(state, action) {
      state.uploadCounts.total_upload_files = action.payload;
      state.uploadCounts.uploaded_files = 0;
      state.uploadCounts.is_upload_complete = false;
    },
    fileUploaded(state) {
      state.uploadCounts.uploaded_files += 1;
    },
    fileFailed(state) {
      state.uploadCounts.failed_upload_count += 1;
    },
    fileCanceled(state) {
      state.uploadCounts.canceled_upload_count += 1;
    },
    uploadComplete(state) {
      state.uploadCounts.is_upload_complete = true;
    },

    resetUploadState(state) {
      state.progress = {};
      state.uploads = {};
      state.uploadCounts = {
        total_upload_files: 0,
        is_upload_complete: true,
        uploaded_files: 0,
        failed_upload_count: 0,
        canceled_upload_count: 0,
      };
    },

    abortUpload(state, action) {
      const preview_id = action.payload;
      const file = state.uploads[preview_id];
      if (file && file.abortController) {
        file.abortController.abort();
        file.status = UPLOAD_STATUS_ENUM.abort;
        file.is_canceled = true;
      }
    },
    abortAll(state) {
      const uploads = cloneDeep(state.uploads);

      Object.keys(uploads).forEach((preview_id) => {
        const file = state.uploads[preview_id];
        if (file && file.status === UPLOAD_STATUS_ENUM.progress && file.abortController) {
          file.abortController.abort();
          file.status = UPLOAD_STATUS_ENUM.abort;
          file.is_canceled = true;
        }
      });
    },
  },
});

export const {
  setProgress,
  setUploadFile,
  resetUploadState,
  uploadComplete,
  uploadStart,
  fileUploaded,
  updateUpload,
  fileFailed,
  abortUpload,
  abortAll,
  expandPopup,
  visiblePopup,
  removeUpload,
  fileCanceled,
} = uploadSlice.actions;

export const cloudUploadReducer = uploadSlice.reducer;
